import { useState, useEffect, useCallback, useRef } from 'react';
import { Project, ProjectPlanStep, ProjectKnowledgeBase, ProjectFile } from '../../../types';
import { MCP_BASE_URL } from '../../../config';

export type StreamPhase = 'genesis' | 'compiling' | 'living';

export interface TelemetryLog {
  stepId: string;
  status: 'processing' | 'completed' | 'dormant' | 'awaiting_approval';
  message: string;
  timestamp: string;
  toolName?: string;
  waitCondition?: string;
}

export interface InterventionPayload {
  stepId: string;
  title: string;
  description: string;
  assetPreview?: string;
  assetType?: 'text' | 'json' | 'markdown';
}

export const useProjectStream = (projectId: string, initialProject: Project, initialSteps: ProjectPlanStep[]) => {
  const [phase, setPhase] = useState<StreamPhase>(initialSteps.length > 0 ? 'living' : 'genesis');
  const [compilationStage, setCompilationStage] = useState(-1);
  
  const [liveSteps, setLiveSteps] = useState<ProjectPlanStep[]>(initialSteps);
  const [telemetry, setTelemetry] = useState<Record<string, TelemetryLog>>({}); // keyed by stepId
  const [liveKnowledge, setLiveKnowledge] = useState<ProjectKnowledgeBase[]>([]);
  const [liveFiles, setLiveFiles] = useState<ProjectFile[]>([]);
  
  const [intervention, setIntervention] = useState<InterventionPayload | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);

  // Starts the project initialization process
  const initializeProject = useCallback(async (objective: string) => {
    setPhase('compiling');
    setCompilationStage(0);

    try {
      // 1. Post to backend
      const res = await fetch(`${MCP_BASE_URL}/api/projects/${projectId}/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ objective })
      });
      
      if (!res.ok) throw new Error("Failed to initialize");

      // 2. Connect to SSE stream
      connectStream();
    } catch (err) {
      console.error("API call failed:", err);
      // Removed fallback simulation to enforce backend execution
    }
  }, [projectId]);

  const connectStream = useCallback(() => {
    if (eventSourceRef.current) return;
    
    const sse = new EventSource(`${MCP_BASE_URL}/api/projects/${projectId}/stream`);
    
    sse.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case 'COMPILATION_UPDATE':
            setCompilationStage(data.payload.stage);
            break;
          case 'COMPILATION_COMPLETE':
            setLiveSteps(data.payload.nodes);
            setPhase('living');
            break;
          case 'TELEMETRY_UPDATE':
            setTelemetry(prev => ({
              ...prev,
              [data.payload.stepId]: data.payload
            }));
            break;
          case 'STEP_COMPLETED':
            setLiveSteps(prev => prev.map(s => s.id === data.payload.stepId ? { ...s, status: 'completed' } : s));
            break;
          case 'WAIT_STATE':
            setLiveSteps(prev => prev.map(s => s.id === data.payload.stepId ? { ...s, status: 'pending' } : s));
            setTelemetry(prev => ({
              ...prev,
              [data.payload.stepId]: {
                stepId: data.payload.stepId,
                status: 'dormant',
                message: `Waiting for: ${data.payload.condition}`,
                timestamp: new Date().toISOString(),
                waitCondition: data.payload.condition
              }
            }));
            break;
          case 'AWAITING_APPROVAL':
            setLiveSteps(prev => prev.map(s => s.id === data.payload.stepId ? { ...s, status: 'pending' } : s));
            setIntervention(data.payload);
            break;
          case 'KNOWLEDGE_EXTRACTED':
            setLiveKnowledge(prev => [data.payload, ...prev]);
            break;
          case 'DELIVERABLE_GENERATED':
            setLiveFiles(prev => [data.payload, ...prev]);
            break;
        }
      } catch(e) {
        console.error("Error parsing SSE data", e);
      }
    };

    sse.onerror = (err) => {
      console.error("SSE connection error", err);
      sse.close();
      eventSourceRef.current = null;
    };

    eventSourceRef.current = sse;
  }, [projectId]);

  useEffect(() => {
    // If it already has steps, connect immediately
    if (initialSteps.length > 0) {
      connectStream();
    }
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [initialSteps, connectStream]);

  // Handle User resolving an intervention
  const submitIntervention = async (action: 'approve' | 'reject' | 'modify', modifications?: string) => {
    if (!intervention) return;
    
    const prevIntervention = intervention;
    setIntervention(null); // Optimistic clear

    try {
      const res = await fetch(`${MCP_BASE_URL}/api/projects/${projectId}/intervention`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stepId: intervention.stepId, action, modifications })
      });
      if (!res.ok) throw new Error("Failed");
    } catch (e) {
      console.error("Failed to submit intervention");
      // Restore if failed
      setIntervention(prevIntervention);
    }
  };

  // The simulated backend fallback was removed since the engine is now active

  return {
    phase,
    setPhase, // allow manual override if needed
    compilationStage,
    liveSteps,
    telemetry,
    liveKnowledge,
    liveFiles,
    intervention,
    initializeProject,
    submitIntervention
  };
};
